/**
 * TanStack Query hooks and key factory for cross-study DTA (data stream) lists.
 */

import type { QueryClient } from "@tanstack/react-query"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { DTADetails, UploadType } from "@/services/dtaApi"
import type { DTAVersion } from "@/types/dta"
import { dtaApiService } from "@/services/dtaApi"
import { studyDtaKeys } from "./useStudyDtas"
import { vendorDtaKeys } from "./useVendorDtas"

type UpdateDtaPayload = Parameters<typeof dtaApiService.updateDTA>[1]
type CreateVersionPayload = Parameters<typeof dtaApiService.createVersion>[1]
type SignDtaPayload = Parameters<typeof dtaApiService.signDTA>[1]
type CreateDtaPayload = Parameters<typeof dtaApiService.createDTA>[1]

export const dtaKeys = {
  all: ["dtas"] as const,
  lists: () => [...dtaKeys.all, "list"] as const,
  list: (userId: string | undefined) => [...dtaKeys.lists(), userId ?? null] as const,
  details: () => [...dtaKeys.all, "detail"] as const,
  detail: (dtaId: string | undefined, version?: string) =>
    [...dtaKeys.details(), dtaId ?? null, version ?? null] as const,
  versionLists: () => [...dtaKeys.all, "versions"] as const,
  versions: (dtaId: string | undefined) => [...dtaKeys.versionLists(), dtaId ?? null] as const,
  version: (dtaId: string | undefined, version: string | undefined) =>
    [...dtaKeys.versions(dtaId), version ?? null] as const,
  signingRecords: (dtaId: string | undefined) =>
    [...dtaKeys.all, "signing-records", dtaId ?? null] as const,
  filings: (dtaId: string | undefined) => [...dtaKeys.all, "filings", dtaId ?? null] as const,
}

/**
 * Which DTA read surfaces a write should refresh. Every field is optional so callers invalidate
 * exactly what their mutation touched.
 */
interface DtaSurfaces {
  /**
   * Refresh this DTA's detail (all cached versions) and, unless `versions: false`, its history.
   */
  dtaId?: string
  /**
   * Set false to skip the version-history refresh (e.g. edits that don't change the version set).
   */
  versions?: boolean
  /**
   * Refresh the study-scoped DTA list.
   */
  studyId?: string
  /**
   * Refresh the vendor-scoped DTA list.
   */
  vendorId?: string
  /**
   * Set false to skip the cross-study "all DTAs" list.
   */
  lists?: boolean
}

/**
 * Single source of truth for invalidating the read surfaces a DTA write affects: the detail and
 * version history for the DTA, and the cross-study / study-scoped / vendor-scoped lists. Callers
 * pass only the ids/flags relevant to their mutation. Workflow-specific keys are layered on by
 * useWorkflow's own helper to avoid a circular import.
 */
export function invalidateDtaSurfaces(queryClient: QueryClient, surfaces: DtaSurfaces): void {
  if (surfaces.dtaId) {
    void queryClient.invalidateQueries({ queryKey: dtaKeys.details() })
    if (surfaces.versions !== false) {
      void queryClient.invalidateQueries({ queryKey: dtaKeys.versions(surfaces.dtaId) })
    }
  }
  if (surfaces.lists !== false) {
    void queryClient.invalidateQueries({ queryKey: dtaKeys.lists() })
  }
  if (surfaces.studyId) {
    void queryClient.invalidateQueries({ queryKey: studyDtaKeys.list(surfaces.studyId) })
  }
  if (surfaces.vendorId) {
    void queryClient.invalidateQueries({ queryKey: vendorDtaKeys.list(surfaces.vendorId) })
  }
}

/**
 * Fetches the full details of a single DTA, optionally at a specific version. Each version is
 * cached separately, so switching versions does not clobber the cache for another.
 */
export function useDtaDetail(dtaId: string | undefined, version?: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: dtaKeys.detail(dtaId, version),
    queryFn: async () => {
      const data = await dtaApiService.getDTADetails(dtaId as string, version)
      // When fetching without a version (latest), also seed the versioned cache key.
      // This prevents a flash of null when the URL later changes to include the version. (#671)
      if (!version && data?.version) {
        queryClient.setQueryData(dtaKeys.detail(dtaId, data.version), data)
      }
      return data
    },
    enabled: !!dtaId,
  })
}

/**
 * Fetches the version history for a DTA, returning just the version array.
 */
export function useDtaVersions(dtaId: string | undefined) {
  return useQuery({
    queryKey: dtaKeys.versions(dtaId),
    queryFn: () => dtaApiService.listVersions(dtaId as string),
    select: (data) => data.versions,
    enabled: !!dtaId,
  })
}

/**
 * Fetches a single historical version snapshot of a DTA.
 */
export function useDtaVersion(dtaId: string | undefined, version: string | undefined) {
  return useQuery({
    queryKey: dtaKeys.version(dtaId, version),
    queryFn: () => dtaApiService.getVersion(dtaId as string, version as string),
    enabled: !!dtaId && !!version,
  })
}

/**
 * Updates a DTA and refreshes every read that displays a mutable field: the detail (all cached
 * versions), the version history, and the cross-study / study-scoped / vendor-scoped DTA lists -
 * because `vendor_name` and `domains` are shown in list rows.
 */
export function useUpdateDta(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateDtaPayload) => dtaApiService.updateDTA(dtaId, payload),
    onMutate: async (payload: UpdateDtaPayload) => {
      // Optimistically merge the edit into every cached version of this DTA's detail so the editor
      // and reupload flows reflect the change instantly. Snapshot for rollback on error.
      await queryClient.cancelQueries({ queryKey: dtaKeys.details() })
      const previous = queryClient.getQueriesData<DTADetails>({ queryKey: dtaKeys.details() })
      queryClient.setQueriesData<DTADetails>({ queryKey: dtaKeys.details() }, (old) =>
        old && old.dta_id === dtaId ? { ...old, ...payload } : old,
      )
      return { previous }
    },
    onError: (_err, _payload, context) => {
      context?.previous?.forEach(([key, data]) => queryClient.setQueryData(key, data))
    },
    onSuccess: (updated: DTADetails) => {
      invalidateDtaSurfaces(queryClient, {
        dtaId,
        studyId: updated.study_id,
        vendorId: updated.vendor_id,
      })
    },
  })
}

/**
 * Creates a new version of a DTA (an amendment). Refetches the version history and the current
 * detail since the new version becomes the active one. Does not touch DTA lists — membership
 * doesn't change, and list-row fields reflect the DTA's live workflow_stage, which the workflow
 * mutations own.
 */
export function useCreateVersion(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateVersionPayload) => dtaApiService.createVersion(dtaId, payload),
    onSuccess: (newVersion) => {
      // Write the server-returned version to the head of the cached list so the amend flow reflects
      // it instantly, then invalidate so the eventual refetch reconciles with server truth.
      queryClient.setQueryData<{ versions: DTAVersion[]; count: number }>(
        dtaKeys.versions(dtaId),
        (old) =>
          old
            ? { versions: [newVersion, ...old.versions], count: old.count + 1 }
            : { versions: [newVersion], count: 1 },
      )
      void queryClient.invalidateQueries({ queryKey: dtaKeys.details() })
      void queryClient.invalidateQueries({ queryKey: dtaKeys.versions(dtaId) })
    },
  })
}

/**
 * Fetches the signing records for a DTA, returning just the records array.
 */
export function useSigningRecords(dtaId: string | undefined) {
  return useQuery({
    queryKey: dtaKeys.signingRecords(dtaId),
    queryFn: () => dtaApiService.getSigningRecords(dtaId as string),
    select: (data) => data.signing_records,
    enabled: !!dtaId,
  })
}

/**
 * Fetches the vTMF filing jobs for a DTA, returning just the filings array.
 */
export function useVtmfFilings(dtaId: string | undefined) {
  return useQuery({
    queryKey: dtaKeys.filings(dtaId),
    queryFn: () => dtaApiService.getVtmfFilings(dtaId as string),
    select: (data) => data.filings,
    enabled: !!dtaId,
  })
}

/**
 * Records a signature on a DTA. Refetches the detail (approval/signing state) and the signing
 * records so the freshly recorded signature appears.
 */
export function useSignDta(dtaId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: SignDtaPayload) => dtaApiService.signDTA(dtaId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: dtaKeys.details() })
      void queryClient.invalidateQueries({ queryKey: dtaKeys.signingRecords(dtaId) })
      void queryClient.invalidateQueries({ queryKey: dtaKeys.filings(dtaId) })
    },
  })
}

/**
 * Creates a DTA in a study and refreshes the study-scoped, cross-study, and vendor-scoped DTA lists
 * so the new stream appears wherever DTAs are listed.
 */
export function useCreateDta(studyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateDtaPayload) => dtaApiService.createDTA(studyId, payload),
    onSuccess: (created) => {
      // A brand-new DTA has no cached detail/versions yet, so only the list surfaces need refreshing.
      invalidateDtaSurfaces(queryClient, { studyId, vendorId: created.vendor_id })
    },
  })
}

/**
 * Presigns and uploads a source document to S3, resolving with its upload_id. Report byte progress
 * via `onProgress` (bind it to component state for a progress bar; driving it through mutation
 * state would thrash the cache). No cache invalidation - callers chain the upload_id into a
 * create/update mutation that owns the invalidation.
 */
export function useUploadDocument(studyId: string) {
  return useMutation({
    mutationFn: ({
      file,
      uploadType,
      onProgress,
    }: {
      file: File
      uploadType: UploadType
      onProgress?: (percent: number) => void
    }) => dtaApiService.presignAndUploadFile(studyId, file, uploadType, onProgress),
  })
}

/**
 * Presigns and uploads an EDC specification, then parses it to discover its forms/domains. Returns
 * the parse result (including the upload_id for a subsequent DTA creation).
 */
export function useUploadAndParseEdc(studyId: string) {
  return useMutation({
    mutationFn: async ({
      file,
      onProgress,
    }: {
      file: File
      onProgress?: (percent: number) => void
    }) => {
      const { upload_id } = await dtaApiService.presignAndUploadFile(
        studyId,
        file,
        "edc",
        onProgress,
      )
      return dtaApiService.parseEdc(studyId, upload_id)
    },
  })
}

/**
 * Presigns and uploads an SDTM mapping specification, then parses it to discover its subject-data
 * SDTM domains. Returns the parse result (including the upload_id for a subsequent DTA creation).
 * Mirrors {@link useUploadAndParseEdc} - the parse result uses the same `forms[]` shape, so the
 * same domain-selection UI renders it.
 */
export function useUploadAndParseSdtm(studyId: string) {
  return useMutation({
    mutationFn: async ({
      file,
      onProgress,
    }: {
      file: File
      onProgress?: (percent: number) => void
    }) => {
      const { upload_id } = await dtaApiService.presignAndUploadFile(
        studyId,
        file,
        "sdtm",
        onProgress,
      )
      return dtaApiService.parseSdtm(studyId, upload_id)
    },
  })
}

/**
 * Fetches every DTA visible to the authenticated user. Uses infinite query to progressively load
 * all pages from the backend, enabling client-side pagination over the accumulated collection.
 */
export function useAllDtasList(userId: string | undefined, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: dtaKeys.list(userId),
    queryFn: ({ pageParam }) =>
      dtaApiService.listAllDTAs({
        limit: pageSize,
        nextToken: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
    enabled: !!userId,
  })
}
